import { NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { Component, HostListener, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import { CurrentUser } from '../../models/user';
import { ToggleService } from '../sidebar/toggle.service';
import { SearchService } from '../../services/search.service';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { StorageService } from '../../services/storage.service';
import { NotificationService } from '../../services/notification.service';
import { GetUnreadNotificationsForUserFilter, MarkNotificationAsDelivered, NotificationForUser, UnreadNotificationForUser } from '../../models/notification';
import { FilterFieldConfig } from '../filter/filter.component';
import { FilterService } from '../filter/services/filter.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FilterDialogComponent } from '../filter/dialog/filter-dialog.component';
import { Subject, takeUntil } from 'rxjs';
import { CrumbsComponent } from '../crumbs/crumbs.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [NgClass, MatMenuModule, MatButtonModule, MatSidenavModule, MatIconModule, MatTooltipModule, MatDialogModule, RouterLink, CrumbsComponent]
})
export class HeaderComponent implements OnInit {
    private destroy$ = new Subject<void>();

    filterScope: string = 'default-scope';
    filterConfig: FilterFieldConfig[] = [];

    unreadNotifications: UnreadNotificationForUser[] = [];
    unreadCount: number = 0;
    user: CurrentUser | null = null;
    // isSidebarToggled
    isSidebarToggled = false;

    // isToggled
    isToggled = false;

    // Responsive breakpoint
    isHandset = false;

    constructor(
        private dialog: MatDialog,
        private router: Router,
        private toggleService: ToggleService,
        private searchService: SearchService,
        private filterService: FilterService,
        public themeService: CustomizerSettingsService,
        private storageService: StorageService,
        private notificationService: NotificationService,
        private breakpointObserver: BreakpointObserver
    ) {
        this.toggleService.isSidebarToggled$.pipe(takeUntil(this.destroy$)).subscribe(isSidebarToggled => {
            this.isSidebarToggled = isSidebarToggled;
        });
        this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
            this.isToggled = isToggled;
        }); 
    }

    ngOnInit(): void {
        // Breakpoint observer for responsive breadcrumbs
        this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          this.isHandset = result.matches;
        });

        this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
          this.user = user;
          if (user) {
            var filter = new GetUnreadNotificationsForUserFilter(user.id, null);
            this.notificationService.getUnreadNotificationsForUser(filter).pipe(takeUntil(this.destroy$)).subscribe({
                next: (data) => {
                  this.unreadNotifications = data;
                  this.unreadCount = this.unreadNotifications.length;
                },
                error: (err) => console.error('Failed to load unread notifications', err)
              });
          }
        });

        this.filterService.filterScope$.pipe(takeUntil(this.destroy$)).subscribe(scope => {
          this.filterScope = scope || 'default-scope';
        });
        this.filterService.filterConfig$.pipe(takeUntil(this.destroy$)).subscribe(config => {
          this.filterConfig = config;
        });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    openFilterDialog() {
      const dialogRef = this.dialog.open(FilterDialogComponent, {
        width: '90vw',
        maxWidth: '600px',
        data: {
           filterScope: this.filterScope,
           filterConfig: this.filterConfig
         }
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
        if (result) {
          this.filterService.applyFilters(result);
        }
      });
    }

    onSearchChange(event: Event) {
        const searchTerm = (event.target as HTMLInputElement).value.trim();
        this.searchService.updateSearchTerm(searchTerm);  // Update the search term
    }

    // Burger Menu Toggle
    toggle() {
        this.toggleService.toggle();
    }

    // Header Sticky
    isSticky: boolean = false;
    @HostListener('window:scroll', ['$event'])
    checkScroll() {
        const scrollPosition = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
        if (scrollPosition >= 50) {
            this.isSticky = true;
        } else {
            this.isSticky = false;
        }
    }

    // Dark Mode
    toggleTheme() {
        this.themeService.toggleTheme();
    }

    viewNotification(notification: UnreadNotificationForUser) {
        if (this.user) {
          const markDeliveredPayload: MarkNotificationAsDelivered = {
            notificationId: notification.id,
            userId: this.user.id
          };
          
          this.notificationService.markNotificationAsDelivered(markDeliveredPayload).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.unreadNotifications = this.unreadNotifications.filter(n => n.id !== notification.id);
              this.unreadCount = this.unreadNotifications.length;

               const notificationForUser: NotificationForUser = {
                id: notification.id,
                title: notification.title,
                message: notification.message,
                fileStorageLocation: notification.fileStorageLocation,
                createdOn: notification.createdOn
              };
      
              this.router.navigate(['association', 'notifications', notification.id, 'view'], {
                state: { notification: notificationForUser },
              });
            },
            error: (err) => console.error('Failed to mark notification as delivered', err)
          });
        }
      }

}